import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import s from './SignalTranslationEquationList.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialSignalTranslationEquationState } from '../../reducers/initialState';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useParams, Link } from 'react-router-dom';
import Pagination from '../Pagination/Pagination';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import SignalTranslationEquationListTable from '../SignalTranslationEquationList/SignalTranslationEquationListTable';
import ModelCategoryConfigurationContentTemplate from '../ModelCategoryTab/ModelCategoryConfigurationContentTemplate';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb'
import { getModelCategoryPermission } from '../../utils';


const SignalTranslationEquationList = ({
  getSignalTranslationEquation,
  setCurrentPage,
  setPageFilter,
  signalTranslationEquationManager = { ...initialSignalTranslationEquationState.signalTranslationEquation },
  intl,
  userProfileWidgets,
  userModelCategories,
  cleanUpSignalTranslationEquation,
  currentTimezone
}) => {
  let filter = signalTranslationEquationManager.filter || '';
  let offset = signalTranslationEquationManager.offset || 0;
  let limit = signalTranslationEquationManager.limit || 1000;
  let { ModelCategoryId } = useParams();

  useEffect(() => {
    getSignalTranslationEquation(offset, limit, filter, ModelCategoryId);
  }, [getSignalTranslationEquation, offset, limit, filter, ModelCategoryId]);

  useEffect(() => {
    return () => { 
      cleanUpSignalTranslationEquation({ ...initialSignalTranslationEquationState.signalTranslationEquation });  
     }
  }, []);


  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };

  const signalTranslationEquationObj = Object.values(signalTranslationEquationManager.SignalTranslationEquationList);

  let messageId = (signalTranslationEquationManager && signalTranslationEquationManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'SIGNALTRANSLATIONEQUATIONEDIT');

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="signalTranslationEquation.signalTranslationEquations" defaultMessage="Signal Translation Equations" /> } // Last item has no href (current page)
  ];

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)


  return (
    <div className={s.signalTranslationEquation}>
      {signalTranslationEquationManager.isLoading && <LoadingSpinner />}
      <Banner
        failureText={messageText}
        showBanner={signalTranslationEquationManager.showBanner}
        status={signalTranslationEquationManager.isOpSuccessful}
        successText={messageText}
      />
      <ModelCategoryConfigurationContentTemplate selectedPage="manageSignalTranslations" userProfileWidgets={userProfileWidgets} >
        <div className={s.contentWrapper}>
          <div className={s.signalTranslationEquationHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="signalTranslationEquation.signalTranslationEquations" defaultMessage="Signal Translation Equations" />
              <span data-unittest="signalTranslationEquationDataCount" className={s.circle}>
                {signalTranslationEquationManager.SignalTranslationEquationList.length}
              </span>
            </div>

          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.titleRows}>
            <Col sm={6} lg={8}>
              <FormattedMessage
                id="signalTranslationEquation.headerInfo"
                defaultMessage="The following equations are defined and used for the signalTranslation of sensors within the Units."
              />
            </Col>
            <Col sm={6} lg={4}>

              {isModelCategoryOwner && <Link to={`/operational-setup/model-category-configuration/${ModelCategoryId}/signal-translation-equation`}>
                <Button className={s.addbutton} style={WidgetVisibility(userProfileWidgets, `SIGNALTRANSLATIONEQUATIONADD`)}>
                  + <FormattedMessage id="signalTranslationEquation.addNew" defaultMessage="+ ADD NEW" />
                </Button>
              </Link>}
            </Col>
          </Row>

          <div className="">
            <Pagination
              data={signalTranslationEquationObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={signalTranslationEquationManager.currentPage}
            >
              <SignalTranslationEquationListTable UpdateAllowed={isUpdateAllowed} currentTimezone={currentTimezone} ModelCategoryId={ModelCategoryId} IsModelCategoryOwner={isModelCategoryOwner} />
            </Pagination>
          </div>
        </div>
      </ModelCategoryConfigurationContentTemplate>
    </div>
  );
};


SignalTranslationEquationList.propTypes = {
  getSignalTranslationEquation: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setPageFilter: PropTypes.func.isRequired,
  currentTimezone: PropTypes.string.isRequired
};

export default injectIntl(SignalTranslationEquationList);
